import React, { useEffect, useRef, useState } from 'react';
import 'bulma/css/bulma.min.css';
import { Chart, registerables } from 'chart.js';
import { fetchSpeedData, fetchLatestSpeedData } from '../../services/api/speedApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

Chart.register(...registerables);

const DeviceModal = ({ isActive, device, onClose }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [speedData, setSpeedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!device || !isActive) return;

    async function loadInitialData() {
      setLoading(true);
      try {
        const data = await fetchSpeedData(device.deviceId);
        if (data) {
          const transformedData = data.map(entry => ({
            time: new Date(entry.serverTime).toLocaleTimeString(),
            speed: entry.speed * 1.852,
          }));
          setSpeedData(transformedData);
        }
      } catch (error) {
        console.error('Error loading speed data:', error);
      } finally {
        setLoading(false);
      }
    }

    loadInitialData();

    // Start interval to fetch new data every second
    intervalRef.current = setInterval(async () => {
      try {
        const latestPoint = await fetchLatestSpeedData(device.deviceId);
        if (latestPoint) {
          const newEntry = {
            time: new Date(latestPoint.serverTime).toLocaleTimeString(),
            speed: latestPoint.speed * 1.852,
          };
          setSpeedData(prevData => {
            const updatedData = [...prevData.slice(1), newEntry]; /// Keep only the latest 300 points
            updateChart(updatedData);
            return updatedData;
          });
        }
      } catch (error) {
        console.error('Error fetching latest data point:', error);
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [device, isActive]);

  const updateChart = (data) => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.data.labels = data.map((_, index) =>
        index === 0 ? "5 min" : index === data.length - 1 ? "teraz" : ""
      );
      chartInstanceRef.current.data.datasets[0].data = data.map(entry => entry.speed);
      chartInstanceRef.current.update();
    }
  };

  useEffect(() => {
    if (!chartRef.current || !isActive || speedData.length === 0) return;

    if (!chartInstanceRef.current) {
      const ctx = chartRef.current.getContext('2d');
      chartInstanceRef.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: speedData.map((_, index) =>
            index === 0 ? "5 minutes ago" : index === speedData.length - 1 ? "now" : ""
          ),
          datasets: [
            {
              label: 'Prędkość (km/h)',
              data: speedData.map(entry => entry.speed),
              borderColor: '#00d1b2',
              backgroundColor: 'rgba(0, 209, 178, 0.2)',
              fill: false,
              tension: 0.4,
            },
          ],
        },
        options: {
          animation: false, // Disable animations for smoother updates
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            x: {
              title: {
                display: true,
                text: 'Czas',
              },
              ticks: {
                callback: function (value, index, values) {
                  return index === 0 ? "5 min" : (index === values.length - 1 ? "Teraz" : "");
                },
                autoSkip: false,
                maxRotation: 0,
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Prędkość (km/h)',
              },
            },
          },
          plugins: {
            legend: {
              display: true,
            },
          },
        },
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [isActive, speedData]);

  if (!device) return null;

  return (
    <div className={`modal ${isActive ? 'is-active' : ''}`}>
      <div className="modal-background" onClick={onClose}></div>
      <div className="modal-content">
        <div className="box">
          <h2 className="title is-4">{device.deviceName}</h2>

          {loading ? (
            <div className="has-text-centered" style={{ marginTop: '20px' }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : (
            <div style={{ marginTop: '20px' }}>
              <h3 className="title is-5">Ostatnie 5 minut</h3>
              <canvas ref={chartRef} width="400" height="200"></canvas>
            </div>
          )}
        </div>
      </div>
      <button className="modal-close is-large" aria-label="close" onClick={onClose}></button>
    </div>
  );
};

export default DeviceModal;
